// Ironbolt — game entry point
//
// Entry flow:
//   ?server=ID  → join that server directly (map comes from relay)
//   ?map=X      → singleplayer with that map (editor or file)
//   (nothing)   → show server browser as home screen

import { Engine } from "./engine/engine.js";
import { createRenderer } from "./game/render.js";
import { Menu } from "./game/ui/menu.js";

const isLocal = location.hostname === "localhost" || location.hostname === "127.0.0.1";
const RELAY_HTTP = isLocal ? "http://localhost:4567" : location.origin;
const RELAY_WS = isLocal ? "ws://localhost:4567" : location.origin.replace(/^http/, "ws");

async function start() {
  const canvas = document.getElementById("game") as HTMLCanvasElement;
  const params = new URLSearchParams(location.search);

  // Override relay URL from params (for production deployment)
  const relayHttp = params.get("relayHttp") ?? RELAY_HTTP;
  const relayWs = params.get("relayWs") ?? RELAY_WS;

  // --- Direct server join: ?server=SERVER_ID ---
  const serverId = params.get("server");
  if (serverId) {
    const password = params.get("password");
    let wsUrl = `${relayWs}/server/${serverId}`;
    if (password) wsUrl += `?password=${encodeURIComponent(password)}`;

    // Build menu for fallback on rejection
    const menu = new Menu(relayHttp, relayWs, {
      onJoin(url: string) {
        const eng = new Engine({
          canvas,
          workerUrl: "dist/worker.js",
          serverUrl: url,
          relayHttp,
          createRenderer,
          debug: true,
          onRejected(reason: string) {
            eng.destroy();
            menu.showBrowser(reason);
          },
        });
        eng.start();
      },
    });

    const engine = new Engine({
      canvas,
      workerUrl: "dist/worker.js",
      serverUrl: wsUrl,
      relayHttp,
      createRenderer,
      debug: true,
      onRejected(reason: string) {
        engine.destroy();
        menu.showBrowser(reason);
      },
    });
    await engine.start();
    return;
  }

  // --- Singleplayer: ?map=X (editor or file) ---
  const mapParam = params.get("map");
  if (mapParam) {
    const sceneJson = await resolveLocalMap(mapParam);
    const engine = new Engine({
      canvas,
      workerUrl: "dist/worker.js",
      sceneJson,
      createRenderer,
      debug: true,
    });
    await engine.start();
    return;
  }

  // --- Home screen: server browser ---
  const menu = new Menu(relayHttp, relayWs, {
    onJoin(wsUrl: string) {
      const engine = new Engine({
        canvas,
        workerUrl: "dist/worker.js",
        serverUrl: wsUrl,
        relayHttp,
        createRenderer,
        debug: true,
        onRejected(reason: string) {
          engine.destroy();
          menu.showBrowser(reason);
        },
      });
      engine.start();
    },
  });
  // ?mapcode=SURF-XXXX → show browser with map detail popup
  const mapCode = params.get("mapcode");
  menu.showBrowser(undefined, mapCode ?? undefined);
}

async function resolveLocalMap(mapParam: string): Promise<string> {
  if (mapParam === "editor") {
    const json = sessionStorage.getItem("ironbolt_editor_scene");
    if (!json) throw new Error("No editor scene in sessionStorage — use the editor Play button");
    return json;
  }
  let url = mapParam;
  if (!url.includes("/")) url = `maps/${url}`;
  if (!url.endsWith(".json")) url += ".json";
  const res = await fetch(url);
  return res.text();
}

start().catch(console.error);
